import { useState } from 'react';
import api from '../../services/api';
import config from '../../config';
import toast from 'react-hot-toast';
import {
  GlobeAltIcon,
  MagnifyingGlassIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  CreditCardIcon,
  ChevronRightIcon,
  ArrowLeftIcon,
  TableCellsIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';

const CREDIT_COST_PER_PAGE = 10;
const MAX_PAGES = 5;

export default function WebScraperSource({ companyId, onSuccess, onBack }) {
  const [step, setStep] = useState(1);

  // Step 1: URL + prompt
  const [url, setUrl] = useState('');
  const [prompt, setPrompt] = useState('');
  const [name, setName] = useState('');

  // Step 2: Pagination
  const [paginationEnabled, setPaginationEnabled] = useState(false);
  const [paginationDepth, setPaginationDepth] = useState(1);
  const [paginationStrategy, setPaginationStrategy] = useState('single');
  const [urlPattern, setUrlPattern] = useState('');

  // Estimate
  const [estimate, setEstimate] = useState(null);
  const [isEstimating, setIsEstimating] = useState(false);

  // Preview
  const [preview, setPreview] = useState(null);
  const [isPreviewing, setIsPreviewing] = useState(false);

  // Scrape
  const [isScraping, setIsScraping] = useState(false);
  const [scrapeResult, setScrapeResult] = useState(null);
  const [scrapeProgress, setScrapeProgress] = useState(null);

  const effectivePages = paginationEnabled ? paginationDepth : 1;
  const isInfiniteScroll = paginationEnabled && paginationStrategy === 'infinite_scroll';
  const maxCost = effectivePages * CREDIT_COST_PER_PAGE;

  const handleEstimate = async () => {
    if (!url) {
      toast.error('Please enter a URL');
      return;
    }
    setIsEstimating(true);
    try {
      const res = await api.post('/web-scraper/estimate', {
        url,
        paginationDepth: effectivePages
      });
      setEstimate(res.data);
      if (!res.data.valid) {
        toast.error(res.data.error || 'Invalid URL');
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to validate URL');
      setEstimate(null);
    } finally {
      setIsEstimating(false);
    }
  };

  const handlePreview = async () => {
    if (!url || !prompt) {
      toast.error('Please enter both URL and extraction prompt');
      return;
    }
    setIsPreviewing(true);
    setPreview(null);
    try {
      const res = await api.post('/web-scraper/preview', { url, prompt }, { timeout: 120000 });
      if (res.data.success) {
        setPreview(res.data);
        setStep(3);
        if (!name) {
          setName(res.data.title || 'Scraped Data');
        }
      } else {
        if (res.data.blocked) {
          toast.error('This website blocked the scraper. No credits were charged.');
        } else if (res.data.noData) {
          toast.error('No data found. Try rephrasing your extraction prompt. No credits were charged.');
        } else {
          toast.error(res.data.error || 'Preview failed');
        }
      }
    } catch (error) {
      const msg = error.response?.data?.error || 'Preview failed';
      if (error.response?.status === 402) {
        toast.error(`Insufficient credits. You need ${CREDIT_COST_PER_PAGE} credits for a preview.`);
      } else {
        toast.error(msg);
      }
    } finally {
      setIsPreviewing(false);
    }
  };

  const handleScrape = async () => {
    if (!name) {
      toast.error('Please enter a name for this data source');
      return;
    }
    setIsScraping(true);
    setScrapeResult(null);
    setScrapeProgress({ phase: 'starting', percentage: 0, message: 'Starting scrape...' });

    let token = null;
    let selectedCompanyId = companyId;
    const authData = localStorage.getItem('neoshift-auth');
    if (authData) {
      try {
        const { state } = JSON.parse(authData);
        token = state?.accessToken;
        if (!selectedCompanyId) {
          selectedCompanyId = state?.selectedCompany?._id || state?.selectedCompany;
        }
      } catch (e) { /* ignore */ }
    }

    try {
      const response = await fetch(`${config.apiUrl}/web-scraper/scrape`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          url,
          prompt,
          name,
          companyId: selectedCompanyId,
          paginationDepth: effectivePages,
          paginationStrategy: paginationEnabled ? paginationStrategy : 'single',
          urlPattern: paginationEnabled && paginationStrategy === 'url_pattern' ? urlPattern : undefined,
          previewPageData: preview ? { data: preview.data, columns: preview.columns, title: preview.title } : undefined
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (response.status === 402) {
          toast.error(`Insufficient credits. This scrape requires up to ${maxCost} credits.`);
        } else {
          toast.error(errorData.error || 'Scrape failed');
        }
        setIsScraping(false);
        setScrapeProgress(null);
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const event = JSON.parse(line.slice(6));

            if (event.type === 'progress') {
              setScrapeProgress(event.data);
            } else if (event.type === 'complete') {
              const result = event.data;
              setScrapeResult(result);
              if (result.success) {
                setScrapeProgress({ phase: 'complete', percentage: 100, message: 'Import complete!' });
                setStep(4);
              } else {
                setScrapeProgress(null);
                if (result.creditsCharged === 0) {
                  toast.error('Scrape failed. No credits were charged.');
                } else {
                  toast.error(result.error || 'Scrape failed');
                }
              }
            } else if (event.type === 'error') {
              setScrapeProgress(null);
              toast.error(event.data?.error || 'Scrape failed');
            }
          } catch (e) {
            console.error('Error parsing SSE:', e);
          }
        }
      }
    } catch (error) {
      console.error('Scrape stream error:', error);
      toast.error('Connection lost during scrape. Check your data sources — it may have completed.');
      setScrapeProgress(null);
    } finally {
      setIsScraping(false);
    }
  };

  const goToStep2 = () => {
    if (!url) {
      toast.error('Please enter a URL');
      return;
    }
    if (!prompt) {
      toast.error('Please describe what data you want to extract');
      return;
    }
    setStep(2);
    handleEstimate();
  };

  return (
    <div className="space-y-6">
      {/* Step indicator */}
      <div className="flex items-center gap-2 text-sm">
        {[
          { num: 1, label: 'URL & Prompt' },
          { num: 2, label: 'Settings' },
          { num: 3, label: 'Preview' },
          { num: 4, label: 'Import' }
        ].map(({ num, label }, idx) => (
          <div key={num} className="flex items-center gap-2">
            {idx > 0 && <ChevronRightIcon className="w-4 h-4 text-surface-400" />}
            <div className={`flex items-center gap-1.5 ${
              step === num
                ? 'text-primary-600 dark:text-primary-400 font-medium'
                : step > num
                  ? 'text-emerald-600 dark:text-emerald-400'
                  : 'text-surface-400'
            }`}>
              <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${
                step === num
                  ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                  : step > num
                    ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300'
                    : 'bg-surface-100 dark:bg-surface-800'
              }`}>
                {step > num ? <CheckCircleIcon className="w-4 h-4" /> : num}
              </span>
              <span className="hidden sm:inline">{label}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Step 1: URL + Prompt */}
      {step === 1 && (
        <div className="space-y-6">
          <div className="card p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-1">
                Target URL
              </label>
              <div className="relative">
                <GlobeAltIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-surface-400" />
                <input
                  type="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://competitor-site.com/pricing"
                  className="input pl-10 w-full"
                />
              </div>
              <p className="mt-1 text-xs text-surface-500">
                Only publicly accessible URLs. No login-protected pages.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-1">
                What data do you want to extract?
              </label>
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder='Extract a list of all subscription tiers. I need the Plan Name, Monthly Price, and the list of included features.'
                rows={4}
                maxLength={2000}
                className="input w-full"
              />
              <p className="mt-1 text-xs text-surface-500 flex justify-between">
                <span>Describe the data structure you want in natural language.</span>
                <span>{prompt.length}/2000</span>
              </p>
            </div>
          </div>

          <div className="flex justify-between">
            <button onClick={onBack} className="btn-ghost">
              <ArrowLeftIcon className="w-4 h-4 mr-2" />
              Back
            </button>
            <button
              onClick={goToStep2}
              disabled={!url || !prompt}
              className="btn-primary"
            >
              Next: Settings
              <ChevronRightIcon className="w-4 h-4 ml-2" />
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Pagination & Cost */}
      {step === 2 && (
        <div className="space-y-6">
          <div className="card p-6 space-y-5">
            <h3 className="font-medium text-surface-900 dark:text-white">Pagination Settings</h3>

            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={paginationEnabled}
                onChange={(e) => {
                  setPaginationEnabled(e.target.checked);
                  if (!e.target.checked) {
                    setPaginationDepth(1);
                    setPaginationStrategy('single');
                  }
                }}
                className="toggle"
              />
              <div>
                <span className="text-sm font-medium text-surface-900 dark:text-white">
                  Enable multi-page scraping
                </span>
                <p className="text-xs text-surface-500">Scrape multiple pages of the same dataset</p>
              </div>
            </label>

            {paginationEnabled && (
              <div className="space-y-4 pl-4 border-l-2 border-primary-200 dark:border-primary-800">
                <div>
                  <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                    Pagination Strategy
                  </label>
                  <div className="space-y-2">
                    <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer hover:bg-surface-50 dark:hover:bg-surface-800 ${
                      paginationStrategy === 'infinite_scroll'
                        ? 'border-primary-300 dark:border-primary-700 bg-primary-50 dark:bg-primary-900/20'
                        : 'border-surface-200 dark:border-surface-700'
                    }`}>
                      <input
                        type="radio"
                        name="strategy"
                        value="infinite_scroll"
                        checked={paginationStrategy === 'infinite_scroll'}
                        onChange={() => setPaginationStrategy('infinite_scroll')}
                        className="mt-1"
                      />
                      <div>
                        <span className="text-sm font-medium text-surface-900 dark:text-white">Infinite Scroll</span>
                        <p className="text-xs text-surface-500 mt-0.5">
                          For pages that load more content as you scroll down (e.g. feeds, directories). The scraper will automatically scroll to load more data.
                        </p>
                      </div>
                    </label>
                    <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer hover:bg-surface-50 dark:hover:bg-surface-800 ${
                      paginationStrategy === 'url_pattern'
                        ? 'border-primary-300 dark:border-primary-700 bg-primary-50 dark:bg-primary-900/20'
                        : 'border-surface-200 dark:border-surface-700'
                    }`}>
                      <input
                        type="radio"
                        name="strategy"
                        value="url_pattern"
                        checked={paginationStrategy === 'url_pattern'}
                        onChange={() => setPaginationStrategy('url_pattern')}
                        className="mt-1"
                      />
                      <div>
                        <span className="text-sm font-medium text-surface-900 dark:text-white">URL Pattern</span>
                        <p className="text-xs text-surface-500 mt-0.5">
                          For sites with separate page URLs (e.g. /page/1, /page/2). Provide a URL template with {'{n}'} as the page number.
                        </p>
                      </div>
                    </label>
                  </div>
                </div>

                {paginationStrategy === 'infinite_scroll' && (
                  <div>
                    <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-1">
                      Scroll depth (max {MAX_PAGES})
                    </label>
                    <input
                      type="number"
                      min={1}
                      max={MAX_PAGES}
                      value={paginationDepth}
                      onChange={(e) => setPaginationDepth(Math.min(MAX_PAGES, Math.max(1, parseInt(e.target.value) || 1)))}
                      className="input w-24"
                    />
                    <p className="mt-1 text-xs text-surface-500">
                      Higher depth scrolls further down the page to load more content. Each level adds ~3 scroll batches. Cost: 10 credits total (single page scrape).
                    </p>
                  </div>
                )}

                {paginationStrategy === 'url_pattern' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-1">
                        Number of pages (max {MAX_PAGES})
                      </label>
                      <input
                        type="number"
                        min={1}
                        max={MAX_PAGES}
                        value={paginationDepth}
                        onChange={(e) => setPaginationDepth(Math.min(MAX_PAGES, Math.max(1, parseInt(e.target.value) || 1)))}
                        className="input w-24"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-1">
                        URL Pattern
                      </label>
                      <input
                        type="text"
                        value={urlPattern}
                        onChange={(e) => setUrlPattern(e.target.value)}
                        placeholder="https://example.com/products?page={n}"
                        className="input w-full"
                      />
                      <p className="mt-1 text-xs text-surface-500">
                        Use {'{n}'} where the page number should go. Pages will be numbered 1 through {paginationDepth}.
                      </p>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Cost Transparency */}
          <div className="card p-6 bg-amber-50 dark:bg-amber-900/10 border-amber-200 dark:border-amber-800">
            <div className="flex items-start gap-3">
              <CreditCardIcon className="w-6 h-6 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <h4 className="font-medium text-amber-900 dark:text-amber-100">Credit Cost</h4>
                <p className="text-sm text-amber-800 dark:text-amber-200 mt-1">
                  {isInfiniteScroll ? (
                    <>This scrape will cost up to <strong>{maxCost} credits</strong> ({effectivePages} scroll depth{effectivePages > 1 ? 's' : ''} &times; {CREDIT_COST_PER_PAGE} credits/depth).</>
                  ) : (
                    <>This scrape will cost up to <strong>{maxCost} credits</strong> ({effectivePages} page{effectivePages > 1 ? 's' : ''} &times; {CREDIT_COST_PER_PAGE} credits/page).</>
                  )}
                </p>
                {estimate && (
                  <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                    Your balance: <strong>{estimate.currentBalance} credits</strong>
                    {!estimate.canAfford && (
                      <span className="text-red-600 dark:text-red-400 ml-2">
                        (Insufficient — need {maxCost - estimate.currentBalance} more)
                      </span>
                    )}
                  </p>
                )}
                <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">
                  You are only charged for pages where data is successfully extracted. If a page is blocked or returns no data, you won't be charged.
                </p>
              </div>
            </div>
          </div>

          <div className="flex justify-between">
            <button onClick={() => setStep(1)} className="btn-ghost">
              <ArrowLeftIcon className="w-4 h-4 mr-2" />
              Back
            </button>
            <button
              onClick={handlePreview}
              disabled={isPreviewing || !url || !prompt || (estimate && !estimate.canAfford)}
              className="btn-primary"
            >
              {isPreviewing ? (
                <>
                  <ArrowPathIcon className="w-4 h-4 mr-2 animate-spin" />
                  Previewing...
                </>
              ) : (
                <>
                  <MagnifyingGlassIcon className="w-4 h-4 mr-2" />
                  Preview Extraction ({CREDIT_COST_PER_PAGE} Cr)
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Preview Results */}
      {step === 3 && preview && (
        <div className="space-y-6">
          <div className="card p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-medium text-surface-900 dark:text-white flex items-center gap-2">
                  <CheckCircleIcon className="w-5 h-5 text-emerald-500" />
                  Preview: {preview.rowCount} row{preview.rowCount !== 1 ? 's' : ''} extracted
                </h3>
                {preview.title && (
                  <p className="text-sm text-surface-500 mt-0.5">From: {preview.title}</p>
                )}
              </div>
              <span className="text-xs bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 px-2 py-1 rounded-full">
                {preview.columns.length} columns
              </span>
            </div>

            {/* Data table */}
            <div className="overflow-x-auto border border-surface-200 dark:border-surface-700 rounded-lg">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-50 dark:bg-surface-800">
                    {preview.columns.map((col) => (
                      <th key={col.name} className="px-3 py-2 text-left font-medium text-surface-600 dark:text-surface-400 whitespace-nowrap">
                        {col.name}
                        <span className="ml-1 text-xs text-surface-400 font-normal">({col.type})</span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-surface-100 dark:divide-surface-800">
                  {preview.data.slice(0, 10).map((row, idx) => (
                    <tr key={idx} className="hover:bg-surface-50 dark:hover:bg-surface-800/50">
                      {preview.columns.map((col) => (
                        <td key={col.name} className="px-3 py-2 text-surface-700 dark:text-surface-300 whitespace-nowrap max-w-[200px] truncate">
                          {row[col.name] != null ? String(row[col.name]) : '—'}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {preview.rowCount > 10 && (
              <p className="text-xs text-surface-500 mt-2">Showing first 10 of {preview.rowCount} rows</p>
            )}
          </div>

          {/* Data Source Name */}
          <div className="card p-6">
            <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-1">
              Data Source Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Competitor Pricing"
              maxLength={100}
              className="input w-full"
            />
            <p className="mt-1 text-xs text-surface-500">This is how the data source will appear in your workspace.</p>
          </div>

          {/* Final cost warning for multi-page */}
          {paginationEnabled && effectivePages > 1 && (
            <div className="card p-4 bg-amber-50 dark:bg-amber-900/10 border-amber-200 dark:border-amber-800">
              <div className="flex items-center gap-2 text-sm text-amber-800 dark:text-amber-200">
                <ExclamationTriangleIcon className="w-5 h-5 text-amber-600 flex-shrink-0" />
                <span>
                  {isInfiniteScroll ? (
                    <>Full import will scroll {effectivePages} depth{effectivePages > 1 ? 's' : ''} deep, costing up to <strong>{maxCost} credits</strong>. The preview above is from the initial scroll only.</>
                  ) : (
                    <>Full import will scrape {effectivePages} pages, costing up to <strong>{maxCost} credits</strong>. The preview above is from the first page only.</>
                  )}
                </span>
              </div>
            </div>
          )}

          {/* Progress bar during import */}
          {isScraping && scrapeProgress && (
            <div className="card p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <ArrowPathIcon className="w-5 h-5 text-primary-500 animate-spin" />
                  <span className="font-medium text-surface-900 dark:text-white text-sm capitalize">
                    {scrapeProgress.phase === 'scraping' ? 'Scraping Pages' :
                     scrapeProgress.phase === 'extracting' ? 'AI Extraction' :
                     scrapeProgress.phase === 'storing' ? 'Saving to BigQuery' :
                     scrapeProgress.phase === 'complete' ? 'Complete' :
                     'Processing'}...
                  </span>
                </div>
                <span className="text-sm text-surface-500">{scrapeProgress.percentage}%</span>
              </div>
              <div className="w-full h-2 bg-surface-200 dark:bg-surface-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary-500 rounded-full transition-all duration-500"
                  style={{ width: `${scrapeProgress.percentage}%` }}
                />
              </div>
              {scrapeProgress.message && (
                <p className="text-xs text-surface-500 dark:text-surface-400 mt-2">{scrapeProgress.message}</p>
              )}
            </div>
          )}

          <div className="flex justify-between">
            <button onClick={() => setStep(2)} disabled={isScraping} className="btn-ghost">
              <ArrowLeftIcon className="w-4 h-4 mr-2" />
              Back to Settings
            </button>
            <button
              onClick={handleScrape}
              disabled={isScraping || !name}
              className="btn-primary"
            >
              {isScraping ? (
                <>
                  <ArrowPathIcon className="w-4 h-4 mr-2 animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  <TableCellsIcon className="w-4 h-4 mr-2" />
                  Import Data{effectivePages > 1 ? ` (up to ${maxCost} Cr)` : ` (${CREDIT_COST_PER_PAGE} Cr)`}
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Step 4: Import Results */}
      {step === 4 && scrapeResult && (
        <div className="space-y-6">
          <div className="card p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                <CheckCircleIcon className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div>
                <h3 className="font-semibold text-surface-900 dark:text-white text-lg">Import Successful</h3>
                <p className="text-sm text-surface-500">Your data has been imported and is ready to use.</p>
              </div>
            </div>

            {/* Stats grid */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
              <div className="bg-surface-50 dark:bg-surface-800 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-surface-900 dark:text-white">
                  {scrapeResult.dataSource.rowCount.toLocaleString()}
                </p>
                <p className="text-xs text-surface-500 mt-1">Rows Imported</p>
              </div>
              <div className="bg-surface-50 dark:bg-surface-800 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-surface-900 dark:text-white">
                  {scrapeResult.dataSource.columnCount}
                </p>
                <p className="text-xs text-surface-500 mt-1">Columns</p>
              </div>
              <div className="bg-surface-50 dark:bg-surface-800 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-surface-900 dark:text-white">
                  {scrapeResult.creditsCharged}
                </p>
                <p className="text-xs text-surface-500 mt-1">Credits Used</p>
              </div>
              <div className="bg-surface-50 dark:bg-surface-800 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-surface-900 dark:text-white">
                  {scrapeResult.pageResults?.length || 1}
                </p>
                <p className="text-xs text-surface-500 mt-1">{isInfiniteScroll ? 'Scroll Depths' : 'Pages Scraped'}</p>
              </div>
            </div>

            {/* Details */}
            <div className="border border-surface-200 dark:border-surface-700 rounded-lg divide-y divide-surface-200 dark:divide-surface-700">
              <div className="flex justify-between items-center px-4 py-3">
                <span className="text-sm text-surface-500">Data Source Name</span>
                <span className="text-sm font-medium text-surface-900 dark:text-white">{scrapeResult.dataSource.name}</span>
              </div>
              <div className="flex justify-between items-center px-4 py-3">
                <span className="text-sm text-surface-500">Source URL</span>
                <a href={url} target="_blank" rel="noopener noreferrer" className="text-sm text-primary-600 dark:text-primary-400 hover:underline truncate max-w-[300px]">
                  {url}
                </a>
              </div>
              <div className="flex justify-between items-center px-4 py-3">
                <span className="text-sm text-surface-500">Strategy</span>
                <span className="text-sm text-surface-900 dark:text-white">
                  {isInfiniteScroll ? `Infinite Scroll (depth ${effectivePages})` : paginationEnabled ? `URL Pattern (${effectivePages} pages)` : 'Single Page'}
                </span>
              </div>
              <div className="flex justify-between items-center px-4 py-3">
                <span className="text-sm text-surface-500">BigQuery Table</span>
                <span className="text-sm font-mono text-surface-600 dark:text-surface-400 truncate max-w-[300px]">
                  {scrapeResult.dataSource.bigQueryTable}
                </span>
              </div>
            </div>

            {/* Page results */}
            {scrapeResult.pageResults && scrapeResult.pageResults.length > 0 && (
              <div className="mt-4">
                <h4 className="text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                  {isInfiniteScroll ? 'Scrape Result' : 'Per-Page Results'}
                </h4>
                <div className="space-y-1.5">
                  {scrapeResult.pageResults.map((pr, idx) => (
                    <div key={idx} className={`flex items-center justify-between px-3 py-2 rounded-lg text-sm ${
                      pr.success
                        ? 'bg-emerald-50 dark:bg-emerald-900/10 text-emerald-800 dark:text-emerald-200'
                        : 'bg-red-50 dark:bg-red-900/10 text-red-800 dark:text-red-200'
                    }`}>
                      <div className="flex items-center gap-2">
                        {pr.success
                          ? <CheckCircleIcon className="w-4 h-4 text-emerald-500" />
                          : <XCircleIcon className="w-4 h-4 text-red-500" />
                        }
                        <span className="truncate max-w-[250px]">{pr.url}</span>
                      </div>
                      <span className="font-medium">
                        {pr.success ? `${pr.rows} rows` : pr.reason}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Scroll warning */}
            {scrapeResult.scrollWarning && (
              <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 rounded-lg">
                <div className="flex items-start gap-2">
                  <ExclamationTriangleIcon className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-amber-800 dark:text-amber-200">{scrapeResult.scrollWarning}</p>
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end">
            <button
              onClick={() => onSuccess(scrapeResult.dataSource)}
              className="btn-primary"
            >
              <TableCellsIcon className="w-4 h-4 mr-2" />
              View Data Source
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
